/* eslint-disable jsx-a11y/no-autofocus */
import { ReactNode, FocusEventHandler, ChangeEventHandler, useState } from "react";
import classnames from "classnames";
import { useFormContext, useWatch } from "react-hook-form";
import ErrorMessage from "../ErrorMessage";

import styles from "./index.module.scss";

type PropsType = React.InputHTMLAttributes<HTMLInputElement> & {
  section?: string;
  id: string;
  label?: string;
  leftIcon?: boolean;
  rightIcon?: boolean;
  showEyeIcon?: boolean;
  placeholder?: string;
  type?: string;
  maxLength?: number;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  disableErrors?: boolean;
  requiredText?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldOptions?: any;
  className?: string;
  onChange?: ChangeEventHandler<HTMLInputElement> | undefined;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  tooltip?: ReactNode;
};

const TextInput = ({
  section,
  id,
  label,
  placeholder,
  leftIcon,
  rightIcon,
  maxLength,
  disabled,
  readOnly,
  autoFocus,
  showEyeIcon,
  disableErrors = false,
  requiredText,
  type,
  className,
  onChange,
  onBlur,
  fieldOptions,
  tooltip,
  ...rest
}: PropsType) => {
  const [isPasswordShown, setPasswordShown] = useState(false);
  const {
    register,
    formState: { errors },
  } = useFormContext();
  const idDotFormat = (() => {
    if (section) {
      return `${section}.${id}`;
    }
    return `${id}`;
  })();
  const error = section && errors[section] ? errors[section][id] : errors[id];
  const noText = !useWatch({ name: idDotFormat });

  const customRegister = register(idDotFormat, {
    maxLength: {
      value: maxLength,
      message: `Cannot exceed ${maxLength} characters`,
    },
    ...fieldOptions,
    ...(requiredText ? { required: requiredText } : {}),
  });

  const togglePasswordVisiblity = () => {
    setPasswordShown(!isPasswordShown);
  }

  return (
    <div className={className}>
      <p className={styles.labelText}>
        {label} {tooltip}
      </p>
      <div className={styles.textInputContainer}>
        <input
          className={classnames(styles.textInput, {
            [styles.textInputError]: !disableErrors && error,
            [styles.textInputFilled]: !noText,
            [styles.textInputLeftIcon]: leftIcon,
            [styles.textInputRightIcon]: rightIcon,
          })}
          aria-describedby={idDotFormat}
          placeholder={placeholder}
          type={(type === 'password' && isPasswordShown) ? 'text' : type}
          id={idDotFormat}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          {...customRegister}
          onChange={(event) => {
            customRegister.onChange(event);
            if (onChange) onChange(event);
          }}
          onBlur={(event) => {
            customRegister.onBlur(event);
            if (onBlur) onBlur(event);
          }}
          {...rest}
        />
        {showEyeIcon && (
          <button
            type="button"
            className={
              classnames(styles.textInputEyeContainer, {
                [styles.textInputEyeIcon]: !isPasswordShown,
                [styles.textInputEyeNoIcon]: isPasswordShown,
              })}
            style={{
              border: "0px",
            }}
            onClick={togglePasswordVisiblity} />
        )}
        {!disableErrors && error && !leftIcon && !rightIcon && (
          <ErrorMessage className={styles.errorMessage} field={idDotFormat}>
            {error.message}
          </ErrorMessage>
        )}
      </div>
    </div>
  );
};

export default TextInput;
